import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import {
  ChartBarIcon,
  ClipboardDocumentListIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, CURRENCY } from '../../config/currency';
import { eodApi } from '../../api/eodApi';
import type { EodSummaryBrief } from '../../api/eodApi';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import EodHistoryCharts from '../../components/finance/EodHistoryCharts';
import FinanceDateRange from '../../components/finance/FinanceDateRange';
import FilteredTotals from '../../components/finance/FilteredTotals';
import {
  PageHeroBand,
  CardContainer,
  LoadingSkeleton,
  EmptyState,
  PrimaryButton,
  StatusChip,
} from '../../components/ui';
import { useFilterParams } from '../../hooks/useFilterParams';

function formatDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(CURRENCY.locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const EOD_HISTORY_DEFAULTS = {
  fromDate: daysAgo(30),
  toDate: daysAgo(0),
  charts: 'off',
  channelId: '', // '' = all channels
};

const EodHistory: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isFinanceOnlyRestricted = Boolean(user?.financeOnlySteward);
  const navigate = useNavigate();

  const [eodFilters, setEodFilters] = useFilterParams(EOD_HISTORY_DEFAULTS, { prefix: 'eod_' });
  const fromDate = eodFilters.fromDate;
  const toDate = eodFilters.toDate;
  const setFromDate = useCallback(
    (v: string) => setEodFilters({ fromDate: v }),
    [setEodFilters],
  );
  const setToDate = useCallback(
    (v: string) => setEodFilters({ toDate: v }),
    [setEodFilters],
  );
  const chartsVisible = (eodFilters.charts as string) !== 'off';
  const setChartsVisible = useCallback(
    (on: boolean) => setEodFilters({ charts: on ? 'on' : 'off' }),
    [setEodFilters],
  );
  const channelFilterId = eodFilters.channelId
    ? Number(eodFilters.channelId)
    : undefined;
  const setChannelFilter = useCallback(
    (id: number | undefined) => setEodFilters({ channelId: id != null ? String(id) : '' }),
    [setEodFilters],
  );
  const [summaries, setSummaries] = useState<EodSummaryBrief[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Channels seen across all loaded summaries — drives chip strip.
  // Deduped by channelId, name from first occurrence.
  const channelChips = useMemo(() => {
    const seen = new Map<number, string>();
    summaries.forEach((s) => {
      (s.channelRevenues ?? []).forEach((c) => {
        if (!seen.has(c.channelId)) seen.set(c.channelId, c.channelName);
      });
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [summaries]);

  // Filter cards: when a channel chip is active, only show days that channel
  // had any gross revenue on. Preserves the "All" view by default.
  const visibleSummaries = useMemo(() => {
    if (channelFilterId == null) return summaries;
    return summaries.filter((s) =>
      (s.channelRevenues ?? []).some(
        (c) => c.channelId === channelFilterId && Number(c.grossAmount) > 0,
      ),
    );
  }, [summaries, channelFilterId]);

  // Chart input: when channel filter active, replace per-day totals with that
  // channel's gross. Net/expenses aren't broken out per channel, so net mirrors
  // gross and expenses zero out — the chart then truthfully reflects only the
  // selected channel's revenue contribution per day.
  const chartFeed = useMemo<EodSummaryBrief[]>(() => {
    if (channelFilterId == null) return visibleSummaries;
    return visibleSummaries.map((s) => {
      const ch = s.channelRevenues?.find((c) => c.channelId === channelFilterId);
      const gross = ch ? Number(ch.grossAmount) : 0;
      return { ...s, grossRevenue: gross, netRevenue: gross, totalExpenses: 0 };
    });
  }, [visibleSummaries, channelFilterId]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await eodApi.getHistory(fromDate, toDate);
      const data = res.data;
      setSummaries(Array.isArray(data) ? data : data.summaries || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load EOD history';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleCardClick = (summary: EodSummaryBrief) => {
    // Restricted finance-only stewards get read-only EOD history. Clicking a
    // confirmed row would jump them into an editable EOD form which the
    // backend rejects on PUT/DELETE; suppress the navigation so the affordance
    // matches reality.
    if (isFinanceOnlyRestricted) return;
    navigate(`/vendor/finance/eod-summary?date=${summary.date}`);
  };

  /* -- Filter-aware totals over the listed (filtered) summaries ---- */

  const totalsItems = useMemo(() => {
    const gross = visibleSummaries.reduce((sum, s) => sum + Number(s.grossRevenue ?? 0), 0);
    const net = visibleSummaries.reduce((sum, s) => sum + Number(s.netRevenue ?? 0), 0);
    const covers = visibleSummaries.reduce((sum, s) => sum + Number(s.covers ?? 0), 0);
    return [
      { label: 'Gross', value: formatCurrency(gross) },
      { label: 'Net', value: formatCurrency(net) },
      { label: 'Covers', value: String(covers) },
      { label: 'Days', value: String(visibleSummaries.length) },
    ];
  }, [visibleSummaries]);

  /* -- Render ------------------------------------------------------ */

  return (
    <Box
      sx={{
        p: '16px 16px 32px',
        backgroundColor: theme.background,
        minHeight: '100vh',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      <FinanceTabBar />

      {/* Header — confident-shell hero band */}
      <PageHeroBand eyebrow="Finance · End of Day" title="EOD History" />

      {/* Summary | History segmented toggle */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <button
          onClick={() => navigate('/vendor/finance/eod-summary')}
          style={{
            padding: '8px 16px',
            borderRadius: 20,
            border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
            backgroundColor: theme.gray?.[100] || '#F0F0F0',
            color: theme.gray?.[500] || '#666',
            fontWeight: 600,
            fontSize: '0.813rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            minHeight: 36,
          }}
        >
          Summary
        </button>
        <button
          onClick={() => navigate('/vendor/finance/eod-history')}
          style={{
            padding: '8px 16px',
            borderRadius: 20,
            border: `1.5px solid ${theme.primary}`,
            backgroundColor: `${theme.primary}20`,
            color: theme.primary,
            fontWeight: 600,
            fontSize: '0.813rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            minHeight: 36,
          }}
        >
          History
        </button>
      </Box>

      {/* Channel filter chips — mirror Aggregators pattern.
          Active chip is heavy (filled primary + bold), inactive is subtle. */}
      {!loading && !error && channelChips.length > 0 && (() => {
        // Active = soft ochre "selected" state (tint + ochre text + ochre ring),
        // not a heavy solid fill: readable at small sizes and consistent with the
        // Summary/History toggle. Inactive stays subtle grey.
        const chipSx = (active: boolean) => ({
          cursor: 'pointer',
          fontWeight: active ? 700 : 500,
          backgroundColor: active ? `${theme.primary}1A` : (theme.gray?.[100] || '#F0F0F0'),
          color: active ? theme.primary : (theme.gray?.[700] || '#444'),
          border: active
            ? `1.5px solid ${theme.primary}`
            : `1px solid ${theme.gray?.[300] || '#D0D0D0'}`,
          '&:hover': {
            backgroundColor: active ? `${theme.primary}26` : (theme.gray?.[200] || '#E0E0E0'),
          },
        });
        return (
          <Box
            data-testid="channel-chips"
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}
          >
            <Chip
              label="All channels"
              onClick={() => setChannelFilter(undefined)}
              sx={chipSx(channelFilterId == null)}
              size="small"
              variant="outlined"
            />
            {channelChips.map((c) => (
              <Chip
                key={c.id}
                label={c.name}
                onClick={() => setChannelFilter(c.id)}
                sx={chipSx(channelFilterId === c.id)}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        );
      })()}

      {/* Active filter banner — confirms what's being filtered + clear shortcut. */}
      {!loading && !error && channelFilterId != null && (() => {
        const sel = channelChips.find((c) => c.id === channelFilterId);
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              mb: 2,
              borderRadius: '8px',
              backgroundColor: `${theme.primary}14`,
              border: `1px solid ${theme.primary}44`,
            }}
          >
            <Typography sx={{ fontSize: '0.813rem', color: theme.primary, fontWeight: 600, flex: 1 }}>
              ● Filtering by <strong>{sel?.name ?? '—'}</strong> · {visibleSummaries.length} of {summaries.length} days · graphs scoped to this channel
            </Typography>
            <Chip
              label="Clear ✕"
              size="small"
              onClick={() => setChannelFilter(undefined)}
              sx={{
                cursor: 'pointer',
                backgroundColor: 'transparent',
                color: theme.primary,
                fontWeight: 700,
                border: `1px solid ${theme.primary}66`,
                '&:hover': { backgroundColor: `${theme.primary}22` },
              }}
            />
          </Box>
        );
      })()}

      {/* Date range filter — the date pair wraps to its own row on narrow
          screens so it never collides with the insights toggle. */}
      <Box
        data-testid="date-filter"
        sx={{
          display: 'flex',
          gap: '10px',
          mb: '20px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <FinanceDateRange
          from={fromDate}
          to={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
          sx={{ flex: '1 1 260px' }}
        />
        <Tooltip
          arrow
          placement="top"
          title={chartsVisible ? 'Collapse the insights strip' : 'Revenue trend · Covers · Status mix'}
        >
          <Chip
            icon={
              chartsVisible
                ? <EyeSlashIcon style={{ width: 14, height: 14 }} />
                : <ChartBarIcon style={{ width: 14, height: 14 }} />
            }
            label={chartsVisible ? 'Hide insights' : 'Show insights'}
            onClick={() => setChartsVisible(!chartsVisible)}
            data-testid="eod-toggle-charts"
            sx={{
              fontSize: '0.75rem',
              height: 32,
              fontWeight: 700,
              cursor: 'pointer',
              px: 0.5,
              flexShrink: 0,
              backgroundColor: chartsVisible ? (theme.gray?.[100] || '#F0F0F0') : `${theme.primary}1f`,
              color: chartsVisible ? (theme.gray?.[600] || '#555') : theme.primary,
              border: chartsVisible ? `1px solid ${theme.gray?.[300] || '#D0D0D0'}` : `1.5px solid ${theme.primary}66`,
              '& .MuiChip-icon': { color: chartsVisible ? (theme.gray?.[600] || '#555') : theme.primary, marginLeft: '6px' },
              '&:hover': { backgroundColor: chartsVisible ? (theme.gray?.[200] || '#E0E0E0') : `${theme.primary}33` },
            }}
          />
        </Tooltip>
      </Box>

      {/* Filter-aware totals over the currently listed summaries */}
      {!loading && !error && visibleSummaries.length > 0 && (
        <FilteredTotals items={totalsItems} data-testid="eod-filtered-totals" />
      )}

      {/* Loading */}
      {loading && <LoadingSkeleton variant="card" count={4} />}

      {/* Error */}
      {error && !loading && (
        <Box
          role="alert"
          sx={{
            backgroundColor: theme.button.danger.bg,
            borderColor: theme.danger,
            color: theme.button.danger.text,
            fontSize: 14,
            p: 2,
            borderRadius: '12px',
            mb: 2,
            border: `1px solid ${theme.danger}`,
          }}
        >
          {error}
          <PrimaryButton
            onClick={fetchHistory}
            sx={{ display: 'block', mt: '10px', fontSize: '0.813rem' }}
            data-testid="retry-button"
          >
            Retry
          </PrimaryButton>
        </Box>
      )}

      {/* Insight charts — scoped to channel chip selection (matches list filter). */}
      {!loading && !error && chartsVisible && chartFeed.length > 0 && (
        <EodHistoryCharts
          summaries={chartFeed}
          onDayClick={isFinanceOnlyRestricted ? undefined : (date) => navigate(`/vendor/finance/eod-summary?date=${date}`)}
        />
      )}

      {/* Empty state */}
      {!loading && !error && summaries.length === 0 && (
        <EmptyState
          icon={<ClipboardDocumentListIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
          title="No EOD summaries found"
          subtitle="Try adjusting your date range or close today's day first."
          actionButton={
            <PrimaryButton
              onClick={() => navigate('/vendor/finance/eod-summary')}
              data-testid="go-today-eod"
            >
              Go to Today&apos;s EOD
            </PrimaryButton>
          }
        />
      )}

      {/* No-match state when channel chip filters out every day */}
      {!loading && !error && summaries.length > 0 && visibleSummaries.length === 0 && (
        <EmptyState
          icon={<MagnifyingGlassIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
          title="No matching days"
          subtitle="This channel had no revenue in the selected range."
        />
      )}

      {/* Summary cards */}
      {!loading && !error && visibleSummaries.map((s) => (
        <CardContainer
          key={s.date}
          role={isFinanceOnlyRestricted ? undefined : 'button'}
          tabIndex={isFinanceOnlyRestricted ? -1 : 0}
          data-testid="eod-card"
          onClick={isFinanceOnlyRestricted ? undefined : () => handleCardClick(s)}
          onKeyDown={isFinanceOnlyRestricted ? undefined : (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCardClick(s);
            }
          }}
          sx={{
            p: 2,
            mb: 1.5,
            cursor: isFinanceOnlyRestricted ? 'default' : 'pointer',
            borderRadius: '16px',
            transition: 'transform 0.15s, box-shadow 0.15s',
            '&:hover': isFinanceOnlyRestricted ? {} : {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            },
          }}
        >
          {/* Top row: date + status */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.938rem',
                fontWeight: 600,
                color: theme.text,
              }}
            >
              {formatDate(s.date)}
            </Typography>
            <StatusChip
              status={s.status}
              variant={s.status === 'CONFIRMED' ? 'success' : 'info'}
            />
          </Box>

          {/* Metrics row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 1,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.688rem', color: theme.gray[500], mb: 0.25 }}>
                Gross Revenue
              </Typography>
              <Typography sx={{ fontSize: '0.938rem', fontWeight: 600, color: theme.text }}>
                {s.grossRevenue != null ? formatCurrency(s.grossRevenue) : '--'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.688rem', color: theme.gray[500], mb: 0.25 }}>
                Net Revenue
              </Typography>
              <Typography sx={{ fontSize: '0.938rem', fontWeight: 600, color: theme.text }}>
                {s.netRevenue != null ? formatCurrency(s.netRevenue) : '--'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.688rem', color: theme.gray[500], mb: 0.25 }}>
                Covers
              </Typography>
              <Typography sx={{ fontSize: '0.938rem', fontWeight: 600, color: theme.text }}>
                {s.covers != null ? s.covers : '--'}
              </Typography>
            </Box>
          </Box>
        </CardContainer>
      ))}
    </Box>
  );
};

export default EodHistory;
